import { ApiRequest, ApiResponse, fetchDataFromAPITyped } from "../services/api-requests";
import { ChargerAttributes } from "../models/charger-model";
import { ChargerHistoryAttributes } from "../models/charger-history-model";

export interface AddPartnerChargerRequest {
    manufacturer_name: string;
    unit_serial: string;
}

export type AddPartnerChargerResponse_t = {
    message: string;
    status?: string; // present on 201/409 path
    associationId?: string;
    chargerId?: string;
    details?: any;
}

export const addChargerToPartner = async (partnerRequest: AddPartnerChargerRequest) => {
    const apiQuery: ApiRequest = {
        route: "charger/partner/addCharger",
        request: {
            method: 'POST',
            entity: 'charger',
            body: partnerRequest
        }
    }
    return await fetchDataFromAPITyped<AddPartnerChargerResponse_t>(apiQuery);
}

// GET /partner/getPartnerChargers → { chargers: ChargerAttributes[] }
export type GetPartnerChargersResponse_t = {
    chargers: ChargerAttributes[];
}

export const getPartnerChargers = async () => {
    const apiQuery: ApiRequest = {
        route: "charger/partner/getPartnerChargers",
        request: {
            method: 'GET',
            entity: 'charger',
            body: '',
        }
    };
    return await fetchDataFromAPITyped<GetPartnerChargersResponse_t>(apiQuery);
}

// POST /partner/getChargerTransactionDetails
export interface GetChargerTransactionDetailsRequest {
    chargerId: string;
    startDate?: string; // ISO string
    endDate?: string;   // ISO string
}

export type GetChargerTransactionDetailsResponse_t = {
    charger: ChargerAttributes;
    permissions: string[];
    chargerHistory?: ChargerHistoryAttributes[];
    partnerEarnings?: { orderCount: number; totalEarningsApplied: number };
    partnerPayoutHistory?: any[];
    partnerPayouts?: any[];
}

export const getChargerTransactionDetails = async (req: GetChargerTransactionDetailsRequest) => {
    const apiQuery: ApiRequest = {
        route: "charger/partner/getChargerTransactionDetails",
        request: {
            method: 'POST',
            entity: 'charger',
            body: req,
        }
    };
    return await fetchDataFromAPITyped<GetChargerTransactionDetailsResponse_t>(apiQuery);
}

// POST /partner/getAllPartnerChargerTransactionDetails → bulk results
export interface GetAllPartnerChargerTxnDetailsRequest {
    startDate?: string;
    endDate?: string;
}

export type GetAllPartnerChargerTxnDetailsResponse_t = {
    earnings?: {
        meanderPartner?: {
            currentPeriod: {
                total: number;
                orderCount: number;
                transactionFees: number;
                kwhMarkupFees: number;
                discountsApplied: number;
            };
            lifetime: {
                total: number;
                orderCount: number;
                transactionFees: number;
                kwhMarkupFees: number;
                discountsApplied: number;
            };
        };
        revenueSplit?: {
            currentPeriod: {
                total: number;
                orderCount: number;
                grossRevenue: number;
                netRevenue: number;
                splitAmount: number;
            };
            lifetime: {
                total: number;
                orderCount: number;
                grossRevenue: number;
                netRevenue: number;
                splitAmount: number;
            };
            currentSplitPercentage?: number;
            splitStatus?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'REVOKED';
        };
        combined: {
            currentPeriodTotal: number;
            lifetimeTotal: number;
            currentPeriodOrderCount: number;
            lifetimeOrderCount: number;
        };
    };
    earningsTrend?: Array<{
        month: string; // 'YYYY-MM'
        meanderPartnerEarnings: number;
        revenueSplitEarnings: number;
        totalEarnings: number;
        orderCount: number;
    }>;
    chargers: Array<{
        charger: ChargerAttributes;
        chargerId: string;
        permissions: string[];
        chargerHistory?: ChargerHistoryAttributes[];
        partnerEarnings?: { orderCount: number; totalEarningsApplied: number };
        partnerPayoutHistory?: any[];
        partnerPayouts?: any[];
        earnings?: {
            meanderPartner?: {
                currentPeriod: {
                    transactionFees: number;
                    kwhMarkupFees: number;
                    discounts: number;
                    total: number;
                    orderCount: number;
                };
                lifetime: {
                    transactionFees: number;
                    kwhMarkupFees: number;
                    discounts: number;
                    total: number;
                    orderCount: number;
                };
            };
            revenueSplit?: {
                currentPeriod: {
                    splitAmount: number;
                    orderCount: number;
                    averagePercentage: number;
                };
                lifetime: {
                    splitAmount: number;
                    orderCount: number;
                    averagePercentage: number;
                };
                currentSplitPercentage?: number;
                splitStatus?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'REVOKED';
            };
            combined: {
                currentPeriodTotal: number;
                lifetimeTotal: number;
                currentPeriodOrderCount: number;
                lifetimeOrderCount: number;
            };
        };
        earningsTrend?: Array<{
            month: string;
            meanderPartnerEarnings: number;
            revenueSplitEarnings: number;
            totalEarnings: number;
            orderCount: number;
        }>;
        lifetimeKwh: number;
        orders?: any[];
    }>;
}

export const getAllPartnerChargerTransactionDetails = async (req: GetAllPartnerChargerTxnDetailsRequest) => {
    const apiQuery: ApiRequest = {
        route: "charger/partner/getAllPartnerChargerTransactionDetails",
        request: {
            method: 'POST',
            entity: 'charger',
            body: req,
        }
    };
    return await fetchDataFromAPITyped<GetAllPartnerChargerTxnDetailsResponse_t>(apiQuery);
}

// POST /partner/removePartnerCharger
export interface RemovePartnerChargerRequest {
    chargerId: string;
}

export type RemovePartnerChargerResponse_t = {
    message: string;
}

export const removePartnerCharger = async (req: RemovePartnerChargerRequest) => {
    const apiQuery: ApiRequest = {
        route: "charger/partner/removePartnerCharger",
        request: {
            method: 'POST',
            entity: 'charger',
            body: req,
        }
    };
    return await fetchDataFromAPITyped<RemovePartnerChargerResponse_t>(apiQuery);
}